import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Armchair,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
} from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { attendanceStatusLocalized } from "../../lib/i18n/portalShell";
import { intlLocale } from "../../lib/i18n/intl";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import type { PortalStrings } from "../../lib/i18n/usePortalLocale";
import { EASE_OUT } from "../../lib/motion";
import {
  canBookSessions,
  clockTime,
  dayLabel,
  defaultBookingDateKey,
  needsSeatClaim,
  bookingWindowLabel,
  slotLabel,
  useCancelBooking,
  useCheckIn,
  useClaimSeat,
  useEffectiveSeatSlots,
  useMySeat,
  useSeatAvailability,
  useWalkInCheckIn,
  type MySeat,
  type SeatBooking,
} from "../../lib/seat/api";
import { useMyAttendance } from "../../lib/attendance/api";
import { SeatBookingFlow } from "./SeatBookingFlow";
import { SeatMapGrid } from "./SeatMapGrid";

export function SeatPanel() {
  const { strings, locale } = usePortalLocale();
  const { data, isLoading, error } = useMySeat();
  const attendanceQuery = useMyAttendance(!error && !isLoading);

  if (isLoading) {
    return (
      <div className="space-y-5">
        <SectionHeader title={strings.yourSeat} level={1} />
        <SectionCard className="space-y-3">
          <div className="bg-muted h-5 w-32 rounded" />
          <div className="bg-muted h-12 w-full rounded-xl" />
          <div className="bg-muted h-4 w-40 rounded" />
        </SectionCard>
      </div>
    );
  }

  if (error) {
    return (
      <NotLinked forbidden={error instanceof ApiError && error.status === 403} strings={strings} />
    );
  }
  if (!data) return <NotLinked forbidden={false} strings={strings} />;

  return (
    <div className="space-y-5">
      {data.plan ? <PlanNotice seat={data} strings={strings} locale={locale} /> : null}
      {needsSeatClaim(data) ? (
        <ClaimPermanentSeat strings={strings} />
      ) : (
        <ReadOnlySeatContent
          data={data}
          strings={strings}
          locale={locale}
          attendanceQuery={attendanceQuery}
        />
      )}
      {canBookSessions(data) && <SeatBookingFlow seat={data} />}
    </div>
  );
}

function ReadOnlySeatContent({
  data,
  strings,
  locale,
  attendanceQuery,
}: {
  data: MySeat;
  strings: PortalStrings;
  locale: "en" | "hi";
  attendanceQuery: ReturnType<typeof useMyAttendance>;
}) {
  return (
    <>
      <Reveal>
        {/* First heading on the Seat tab, so it is the page title. */}
        <SectionHeader title={strings.seatToday} level={1} />
        {data.today ? (
          <TodayCard booking={data.today} strings={strings} locale={locale} />
        ) : data.plan?.kind === "no_seat" ? null : (
          <SectionCard className="flex flex-col items-center gap-3 py-9 text-center">
            <span className="bg-muted text-muted-foreground grid size-14 place-items-center rounded-2xl">
              <Armchair className="size-7" />
            </span>
            <div className="space-y-1">
              <p className="font-display text-lg font-semibold">{strings.nothingHeldToday}</p>
              <p className="text-muted-foreground mx-auto max-w-xs text-sm">
                {data.assigned
                  ? strings.nothingHeldHintAssigned(
                      placeLabel(data.assigned.seat_number, data.assigned.zone_name, strings),
                    )
                  : canBookSessions(data)
                    ? strings.nothingHeldHintBookBelow
                    : strings.nothingHeldHintUnassigned}
              </p>
            </div>
          </SectionCard>
        )}
      </Reveal>

      {data.assigned && (
        <Reveal index={1}>
          <SectionHeader title={strings.yourRegularSeat} />
          <SectionCard className="flex items-center gap-3">
            <span className="bg-primary/12 text-primary grid size-11 shrink-0 place-items-center rounded-xl">
              <Armchair className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="font-semibold">
                {data.assigned.seat_number ?? strings.seatDetailsAtDesk}
              </p>
              <p className="text-muted-foreground text-xs">
                {data.assigned.zone_name ?? strings.zoneAtCentre}
              </p>
            </div>
          </SectionCard>
        </Reveal>
      )}

      {data.upcoming.length > 0 && (
        <Reveal index={2}>
          <SectionHeader title={strings.comingUp} />
          <SectionCard className="divide-border/60 divide-y p-0">
            {data.upcoming.map((b) => (
              <div key={b.id} className="px-4 py-3.5">
                <CancelBookingControl
                  bookingId={b.id}
                  triggerText={strings.cancelBooking}
                  triggerLabel={strings.cancelBookingOn(
                    `${dayLabel(b.date_key, intlLocale(locale))}, ${bookingWindowLabel(b, locale)}`,
                  )}
                  strings={strings}
                >
                  <span className="text-muted-foreground bg-muted grid size-10 shrink-0 place-items-center rounded-xl">
                    <CalendarDays className="size-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {dayLabel(b.date_key, intlLocale(locale))}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {bookingWindowLabel(b, locale)}
                      {b.place?.seat_number ? ` · ${b.place.seat_number}` : ""}
                    </p>
                  </div>
                </CancelBookingControl>
              </div>
            ))}
          </SectionCard>
        </Reveal>
      )}

      {attendanceQuery.data && (
        <Reveal index={3}>
          <SectionHeader title={strings.attendance} />
          <SectionCard className="space-y-4">
            {attendanceQuery.data.today ? (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{strings.attendanceToday}</p>
                  <p className="text-muted-foreground text-xs">
                    {attendanceStatusLocalized(locale, attendanceQuery.data.today.status)}
                    {attendanceQuery.data.today.late_minutes > 0
                      ? ` · ${strings.minLate(attendanceQuery.data.today.late_minutes)}`
                      : ""}
                  </p>
                </div>
                <span className="bg-primary/12 text-primary rounded-full px-2.5 py-1 text-xs font-semibold">
                  {attendanceStatusLocalized(locale, attendanceQuery.data.today.status)}
                </span>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{strings.attendanceNoneToday}</p>
            )}
            {/* Each figure and its caption were separate paragraphs, so a screen
                reader announced "100" and "Last 30 days" as unrelated
                fragments. One labelled group per stat keeps them together. */}
            <div className="border-border/60 grid grid-cols-2 gap-3 border-t pt-4">
              <div
                role="group"
                aria-label={strings.attendanceStat(
                  `${attendanceQuery.data.summary.attendance_percentage}%`,
                  strings.last30Days,
                )}
              >
                <p className="font-display text-lg font-semibold tabular-nums" aria-hidden="true">
                  {attendanceQuery.data.summary.attendance_percentage}%
                </p>
                <p className="text-muted-foreground text-xs" aria-hidden="true">
                  {strings.last30Days}
                </p>
              </div>
              <div
                role="group"
                aria-label={strings.attendanceStat(
                  `${attendanceQuery.data.summary.present_days + attendanceQuery.data.summary.late_days} / ${attendanceQuery.data.summary.total_days}`,
                  strings.daysMarked,
                )}
              >
                <p className="font-display text-lg font-semibold tabular-nums" aria-hidden="true">
                  {attendanceQuery.data.summary.present_days +
                    attendanceQuery.data.summary.late_days}
                  <span className="text-muted-foreground text-sm font-normal">
                    {" "}
                    / {attendanceQuery.data.summary.total_days}
                  </span>
                </p>
                <p className="text-muted-foreground text-xs" aria-hidden="true">
                  {strings.daysMarked}
                </p>
              </div>
            </div>
          </SectionCard>
        </Reveal>
      )}
    </>
  );
}

/** One line telling the learner how their plan works, in their words. */
function planSummary(seat: MySeat, strings: PortalStrings, locale: "en" | "hi"): string {
  const plan = seat.plan;
  if (!plan) return "";
  switch (plan.kind) {
    case "fixed_seat": {
      const place = seat.assigned ?? {
        seat_number: plan.seat_number,
        zone_name: plan.zone_name,
      };
      return strings.planFixedSeat(placeLabel(place.seat_number, place.zone_name, strings));
    }
    case "first_come":
      return strings.planFirstCome;
    case "fixed_hours":
      if (plan.window_start && plan.window_end) {
        return strings.planFixedHoursWindow(
          clockTime(plan.window_start),
          clockTime(plan.window_end),
        );
      }
      return strings.planFixedHoursShifts(
        plan.slot_keys.map((k) => slotLabel(k, locale)).join(", ") || strings.assignedByCentre,
      );
    case "flexible_hours":
      return strings.planFlexibleHours;
    case "zone_only":
      return strings.planZoneOnly(plan.zone_name ?? strings.zoneAtCentre);
    case "no_seat":
      return strings.planNoSeat;
  }
}

function PlanNotice({
  seat,
  strings,
  locale,
}: {
  seat: MySeat;
  strings: PortalStrings;
  locale: "en" | "hi";
}) {
  const reduce = useReducedMotion();
  const walkIn = useWalkInCheckIn();
  const [failed, setFailed] = React.useState(false);
  const plan = seat.plan!;
  const checkedInToday = seat.today?.status === "checked_in" || walkIn.isSuccess;

  return (
    <Reveal>
      <SectionCard className="space-y-3" data-testid="seat-plan-notice">
        <div className="flex items-start gap-3">
          <span className="bg-primary/12 text-primary grid size-10 shrink-0 place-items-center rounded-xl">
            {plan.kind === "no_seat" ? (
              <MapPin className="size-5" />
            ) : plan.kind === "zone_only" ? (
              <Building2 className="size-5" />
            ) : plan.kind === "fixed_hours" || plan.kind === "flexible_hours" ? (
              <Clock className="size-5" />
            ) : (
              <Armchair className="size-5" />
            )}
          </span>
          <div className="min-w-0 space-y-0.5">
            <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              {strings.planTitle}
            </p>
            <p className="text-sm font-semibold">{planSummary(seat, strings, locale)}</p>
            {plan.valid_until ? (
              <p className="text-muted-foreground text-xs">
                {strings.planValidUntil(dayLabel(plan.valid_until, intlLocale(locale)))}
              </p>
            ) : null}
          </div>
        </div>

        {plan.kind === "no_seat" ? (
          <AnimatePresence mode="wait" initial={false}>
            {checkedInToday ? (
              <motion.p
                key="walk-in-done"
                initial={reduce ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: reduce ? 0 : 0.2, ease: EASE_OUT }}
                className="text-primary flex items-center gap-1.5 text-sm font-semibold"
              >
                <CheckCircle2 className="size-4" />
                {strings.walkInCheckedIn}
              </motion.p>
            ) : (
              <motion.div
                key="walk-in-btn"
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
                transition={{ duration: reduce ? 0 : 0.15, ease: EASE_OUT }}
              >
                <Button
                  onClick={() => {
                    setFailed(false);
                    walkIn.mutate(undefined, { onError: () => setFailed(true) });
                  }}
                  disabled={walkIn.isPending}
                  className="h-11 w-full rounded-xl font-semibold transition-transform duration-150 ease-out active:scale-[0.97]"
                >
                  {walkIn.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    strings.walkInCheckIn
                  )}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        ) : null}
        {failed ? (
          <p role="alert" className="text-destructive text-center text-xs font-medium">
            {strings.walkInCheckInFailed}
          </p>
        ) : null}
      </SectionCard>
    </Reveal>
  );
}

function ClaimPermanentSeat({ strings }: { strings: PortalStrings }) {
  const reduce = useReducedMotion();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const claim = useClaimSeat();
  // A permanent claim isn't shift-bound, but the seat map is read through
  // a shift the centre actually runs today.
  const today = defaultBookingDateKey();
  const slotsQuery = useEffectiveSeatSlots(today);
  const firstSlotKey = slotsQuery.data?.[0]?.key ?? "";
  const availability = useSeatAvailability(today, firstSlotKey, true);

  const zoneNames = React.useMemo(
    () => new Map((availability.data?.zones ?? []).map((z) => [z.zone_id, z.zone_name])),
    [availability.data?.zones],
  );
  const seats = availability.data?.seats ?? [];

  function handleClaim() {
    if (!selectedId) return;
    setErrorMsg(null);
    claim.mutate(selectedId, {
      onError: (err) => {
        if (err instanceof ApiError && err.details?.code === "seat.student_already_assigned") {
          setErrorMsg(strings.alreadyHavePermanentSeat);
        } else if (err instanceof ApiError && err.status === 403) {
          setErrorMsg(strings.bookingNotAllowed);
        } else {
          setErrorMsg(strings.claimFailed);
        }
      },
    });
  }

  return (
    <>
      <Reveal>
        <SectionHeader title={strings.claimYourSeat} />
        <SectionCard className="space-y-4">
          <p className="text-muted-foreground text-sm">{strings.claimYourSeatHint}</p>
          {slotsQuery.isError && (
            <div className="flex flex-col items-center gap-2 text-center">
              <p role="alert" className="text-destructive text-sm font-medium">
                {strings.shiftsLoadFailed}
              </p>
              <Button variant="outline" size="sm" onClick={() => void slotsQuery.refetch()}>
                {strings.retry}
              </Button>
            </div>
          )}
          {slotsQuery.isSuccess && slotsQuery.data.length === 0 && (
            <p className="text-muted-foreground text-center text-sm">{strings.noShiftsOnDay}</p>
          )}
          {(slotsQuery.isLoading || availability.isLoading) && (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
              <Loader2 className="size-4 animate-spin" />
              {strings.loadingAvailability}
            </div>
          )}
          {seats.length > 0 && (
            <SeatMapGrid
              seats={seats}
              selectedId={selectedId}
              onSelect={setSelectedId}
              zoneNames={zoneNames}
              strings={{
                seatTaken: strings.seatTaken,
                seatAvailable: strings.seatAvailable,
                yourSeat: strings.yourSeatLabel,
              }}
            />
          )}
          <AnimatePresence mode="wait">
            {selectedId && (
              <motion.div
                key="claim-btn"
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
                transition={{ duration: reduce ? 0 : 0.2, ease: EASE_OUT }}
              >
                <Button
                  onClick={handleClaim}
                  disabled={claim.isPending}
                  className="h-11 w-full rounded-xl font-semibold transition-transform duration-150 ease-out active:scale-[0.97]"
                >
                  {claim.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    strings.claimSeat
                  )}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
          {errorMsg && (
            <p role="alert" className="text-destructive text-center text-xs font-medium">
              {errorMsg}
            </p>
          )}
        </SectionCard>
      </Reveal>
    </>
  );
}

function TodayCard({
  booking,
  strings,
  locale,
}: {
  booking: SeatBooking;
  strings: PortalStrings;
  locale: "en" | "hi";
}) {
  const reduce = useReducedMotion();
  const checkIn = useCheckIn();
  const [failed, setFailed] = React.useState(false);
  const checkedIn = booking.status === "checked_in";

  return (
    <SectionCard className="space-y-4">
      <div className="flex items-center gap-3">
        <motion.span
          className="bg-primary/12 text-primary grid size-12 shrink-0 place-items-center rounded-2xl"
          animate={
            checkedIn && !reduce
              ? { scale: [1, 1.06, 1], transition: { duration: 0.35, ease: EASE_OUT } }
              : undefined
          }
        >
          <Armchair className="size-6" />
        </motion.span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-semibold leading-tight">
            {booking.place?.seat_number ?? strings.seatDetailsAtDesk}
          </p>
          <p className="text-muted-foreground truncate text-sm">
            {booking.place?.zone_name ?? strings.zoneAtCentre}
          </p>
        </div>
        <AnimatePresence mode="wait">
          {checkedIn && (
            <motion.span
              key="checked-in"
              initial={reduce ? false : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
              transition={{ duration: reduce ? 0 : 0.2, ease: EASE_OUT }}
              className="bg-primary/12 text-primary flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
            >
              <CheckCircle2 className="size-3.5" />
              {strings.checkedIn}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
        <Clock className="size-4" />
        {bookingWindowLabel(booking, locale)}
      </div>

      <AnimatePresence mode="wait">
        {!checkedIn && booking.status === "held" && (
          <motion.div
            key="check-in-btn"
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={{ duration: reduce ? 0 : 0.2, ease: EASE_OUT }}
          >
            <Button
              onClick={() => {
                setFailed(false);
                checkIn.mutate(booking.id, { onError: () => setFailed(true) });
              }}
              disabled={checkIn.isPending}
              className="h-11 w-full rounded-xl font-semibold transition-transform duration-150 ease-out active:scale-[0.97]"
            >
              {checkIn.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                strings.imHereCheckIn
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {failed && (
        <p role="alert" className="text-destructive text-center text-xs font-medium">
          {strings.checkInFailed}
        </p>
      )}

      {booking.status === "held" && (
        <CancelBookingControl
          bookingId={booking.id}
          triggerText={strings.cancelTodayBooking}
          triggerLabel={strings.cancelTodayBooking}
          strings={strings}
        />
      )}
    </SectionCard>
  );
}

/**
 * Cancel with an inline confirm: a mis-tap on a phone must not give the
 * seat away, and a dialog is heavier than the decision deserves.
 */
function CancelBookingControl({
  bookingId,
  triggerText,
  triggerLabel,
  strings,
  children,
}: {
  bookingId: string;
  triggerText: string;
  triggerLabel: string;
  strings: PortalStrings;
  children?: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  const cancel = useCancelBooking();
  const [confirming, setConfirming] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {children}
        {!confirming && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={triggerLabel}
            onClick={() => {
              setFailed(false);
              setConfirming(true);
            }}
            className={
              children
                ? "text-muted-foreground hover:text-destructive shrink-0 transition-[color,transform] duration-150 ease-out active:scale-[0.97]"
                : "text-muted-foreground hover:text-destructive h-10 w-full rounded-xl transition-[color,transform] duration-150 ease-out active:scale-[0.97]"
            }
          >
            {triggerText}
          </Button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {confirming && (
          <motion.div
            key="confirm-cancel"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: reduce ? 0 : 0.18, ease: EASE_OUT }}
            className="bg-muted/60 space-y-3 rounded-xl p-3"
          >
            <p className="text-sm font-medium">{strings.cancelBookingPrompt}</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={cancel.isPending}
                onClick={() => setConfirming(false)}
                className="flex-1 rounded-lg transition-transform duration-150 ease-out active:scale-[0.97]"
              >
                {strings.keepBooking}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={cancel.isPending}
                onClick={() => {
                  setFailed(false);
                  cancel.mutate(bookingId, {
                    onSuccess: () => setConfirming(false),
                    onError: () => setFailed(true),
                  });
                }}
                className="flex-1 rounded-lg transition-transform duration-150 ease-out active:scale-[0.97]"
              >
                {cancel.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  strings.confirmCancelBooking
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {failed && (
        <p role="alert" className="text-destructive text-center text-xs font-medium">
          {strings.cancelBookingFailed}
        </p>
      )}
    </div>
  );
}

function NotLinked({ forbidden, strings }: { forbidden: boolean; strings: PortalStrings }) {
  return (
    <div className="space-y-5">
      <Reveal>
        <SectionHeader title={strings.yourSeat} level={1} />
        <SectionCard className="flex flex-col items-center gap-3 py-10 text-center">
          <span className="bg-muted text-muted-foreground grid size-14 place-items-center rounded-2xl">
            <Armchair className="size-7" />
          </span>
          <div className="space-y-1">
            <p className="font-display text-lg font-semibold">
              {forbidden ? strings.notLinkedTitle : strings.seatUnavailableTitle}
            </p>
            <p className="text-muted-foreground mx-auto max-w-xs text-sm">
              {forbidden ? strings.notLinkedBody : strings.seatLoadFailedBody}
            </p>
          </div>
        </SectionCard>
      </Reveal>

      <Reveal index={1}>
        <SectionHeader title={strings.whatAppearsHere} />
        <SectionCard className="divide-border/60 divide-y p-0">
          <ExplainRow
            icon={<Armchair className="size-5" />}
            title={strings.explainSeatTitle}
            body={strings.explainSeatBody}
          />
          <ExplainRow
            icon={<Clock className="size-5" />}
            title={strings.explainShiftTitle}
            body={strings.explainShiftBody}
          />
          <ExplainRow
            icon={<Building2 className="size-5" />}
            title={strings.explainDeskTitle}
            body={strings.explainDeskBody}
          />
        </SectionCard>
      </Reveal>
    </div>
  );
}

function placeLabel(seat: string | null, zone: string | null, strings: PortalStrings): string {
  if (seat && zone) return strings.inZone(seat, zone);
  return seat ?? zone ?? strings.assignedByCentre;
}

function ExplainRow({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <span className="text-muted-foreground bg-muted grid size-10 shrink-0 place-items-center rounded-xl">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-muted-foreground text-xs">{body}</p>
      </div>
    </div>
  );
}
